import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Switch,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CmBox,
  CmButton,
  CmFlex,
  CmInput,
  CmLongLoading,
  CmMessageCard,
  CmSelect,
  CmText,
  QuestionOutlineIcon,
  UpDownIcon,
} from '@/components/uiParts';
import { LinkItem } from '@/domain/models/LinkItem';
import { BULK_SOLDOUT_V2_ENTERPRISE_IDS, isProd } from '@/global/env';
import { redirect404Page } from '@/lib/AccessControl';
import { getClient } from '@/lib/restClient';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  useGetBrandSalesStateGroupRules,
  useUpsertBrandSalesStateGroupRules,
} from '@/services/BrandSalesStateGroupRules/hooks';
import { useEnterprise } from '@/services/Enterprise/hooks/useEnterprise';
import { LinkItemService } from '@/services/LinkItem';

import {
  matchesRule,
  matchExample,
  MatchRule,
  matchTypeLabel,
  parseBulkSoldoutGroupDef,
  SoldoutGroup,
} from './parseBulkSoldoutGroupDef';

// --- 型定義 ---

interface FlatItem {
  name: string;
  dsp: string;
  isTopping: boolean;
  key: string;
}

// --- LinkItem[] → FlatItem[] 変換 ---

const linkItemsToFlatItems = (
  items: LinkItem[],
  storeName?: string
): FlatItem[] => {
  const result: FlatItem[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    // 店舗フィルタ: store_name で絞り込み
    if (storeName) {
      const hasStore = item.linked_store_items.some(
        (si) => si.store_name === storeName
      );
      if (!hasStore) continue;
    }

    const key = `${item.delivery_service_name}:${item.item_name}`;
    if (seen.has(key)) continue;
    seen.add(key);

    result.push({
      name: item.item_name,
      dsp: item.delivery_service_name,
      isTopping: item.is_topping ?? false,
      key,
    });
  }

  return result;
};

// --- 型定義 ---

interface GroupWithMatches {
  group: SoldoutGroup;
  matches: FlatItem[];
}

// --- 小コンポーネント ---

const MatchTypeBadge: React.FC<{ rule: MatchRule }> = ({ rule }) => {
  const colorScheme =
    rule.matchType === 'exact'
      ? 'green'
      : rule.matchType === 'prefix'
      ? 'blue'
      : rule.matchType === 'suffix'
      ? 'teal'
      : 'purple';
  return (
    <Badge colorScheme={colorScheme} fontSize="xs" mr={1}>
      {matchTypeLabel(rule.matchType)}
    </Badge>
  );
};

// --- 操作ガイドモーダル ---

const HelpModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
    <ModalOverlay />
    <ModalContent>
      <ModalHeader borderBottom="1px solid" borderColor="gray.200" pb={3}>
        操作ガイド
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody py={5}>
        <CmText fontSize="sm" color="gray.700" mb={4}>
          このページでは、同時に品切れになる商品をまとめて「グループ」として登録できます。
          例えば「チーズが欠品したとき、チーズを使う商品をまとめて品切れにしたい」といった場合に使います。
        </CmText>
        <CmBox borderTop="2px solid" borderColor="gray.300" pt={3} mb={4}>
          <CmText fontWeight="bold" fontSize="sm" mb={3}>
            グループの書き方
          </CmText>
          <CmFlex mb={3} align="baseline">
            <CmBox
              bg="blue.600"
              color="white"
              fontWeight="bold"
              fontSize="xs"
              borderRadius="full"
              w="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              mr={2}
            >
              1
            </CmBox>
            <CmBox>
              <CmText fontSize="sm" mb={1}>
                グループ名を <strong>[ ]</strong> で囲んで書く
              </CmText>
              <CmBox
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="sm"
                px={3}
                py={1}
                fontFamily="monospace"
                fontSize="sm"
                display="inline-block"
              >
                [チーズ欠品]
              </CmBox>
            </CmBox>
          </CmFlex>
          <CmFlex align="baseline">
            <CmBox
              bg="blue.600"
              color="white"
              fontWeight="bold"
              fontSize="xs"
              borderRadius="full"
              w="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              mr={2}
            >
              2
            </CmBox>
            <CmBox>
              <CmText fontSize="sm" mb={2}>
                その下に、まとめたい商品名の条件を1行ずつ書く
              </CmText>
              <CmBox
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                fontSize="xs"
                color="gray.700"
              >
                {[
                  {
                    pattern: '商品名',
                    label: '完全一致',
                    desc: 'その名前とまったく同じ商品',
                    example: '例）チーズバーガー → 「チーズバーガー」のみ',
                    color: 'green',
                  },
                  {
                    pattern: '商品名*',
                    label: '前方一致',
                    desc: 'その文字で始まる商品',
                    example:
                      '例）チーズ* → 「チーズバーガー」「チーズドッグ」など',
                    color: 'blue',
                  },
                  {
                    pattern: '*商品名',
                    label: '後方一致',
                    desc: 'その文字で終わる商品',
                    example:
                      '例）*バーガー → 「チーズバーガー」「てりやきバーガー」など',
                    color: 'teal',
                  },
                  {
                    pattern: '*商品名*',
                    label: '部分一致',
                    desc: 'その文字を含む商品すべて',
                    example:
                      '例）*チーズ* → 「チーズバーガー」「ダブルチーズ」など',
                    color: 'purple',
                  },
                ].map((row, i) => (
                  <CmFlex
                    key={i}
                    align="flex-start"
                    py={2}
                    borderBottom={i < 3 ? '1px solid' : 'none'}
                    borderColor="gray.200"
                  >
                    <CmBox minW="90px" mr={3}>
                      <Badge colorScheme={row.color} mb={1}>
                        {row.label}
                      </Badge>
                      <CmText
                        fontFamily="monospace"
                        fontWeight="bold"
                        fontSize="xs"
                      >
                        {row.pattern}
                      </CmText>
                    </CmBox>
                    <CmBox>
                      <CmText fontSize="xs" color="gray.600">
                        {row.desc}
                      </CmText>
                      <CmText fontSize="xs" color="gray.400" mt={0.5}>
                        {row.example}
                      </CmText>
                    </CmBox>
                  </CmFlex>
                ))}
              </CmBox>
            </CmBox>
          </CmFlex>
        </CmBox>
        <CmBox borderTop="2px solid" borderColor="gray.300" pt={3}>
          <CmText fontWeight="bold" fontSize="sm" mb={2}>
            記述例
          </CmText>
          <CmBox
            bg="gray.800"
            color="gray.100"
            borderRadius="md"
            p={4}
            fontFamily="monospace"
            fontSize="sm"
            whiteSpace="pre"
            lineHeight="tall"
          >
            {`[チーズ欠品]\n*チーズ*\n*Cheese*\n\n[ドリンク欠品]\n*コーラ*\n*Coke*\n*コーヒー*`}
          </CmBox>
        </CmBox>
      </ModalBody>
    </ModalContent>
  </Modal>
);

// --- メインページ ---

const LinkItemsV2Page: React.FC = () => {
  const { managerAccountMe } = useManagerAccountMe();
  const history = useHistory();

  redirect404Page('setting_sales_state_group', history, managerAccountMe);

  if (
    isProd &&
    managerAccountMe !== undefined &&
    !BULK_SOLDOUT_V2_ENTERPRISE_IDS.includes(managerAccountMe.enterprise_id)
  ) {
    history.push('/404');
  }

  const { enterprise, isLoading: isEnterpriseLoading } = useEnterprise();
  const brands = useMemo(() => enterprise?.brands ?? [], [enterprise]);

  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>(
    undefined
  );
  const [selectedStoreName, setSelectedStoreName] = useState<string>('');
  const [rulesText, setRulesText] = useState<string>('');
  const [allItems, setAllItems] = useState<LinkItem[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState(false);

  const {
    rulesData,
    isLoading: isRulesLoading,
    refetch,
  } = useGetBrandSalesStateGroupRules(selectedBrandId);
  const { upsertRules, isLoading: isUpsertLoading } =
    useUpsertBrandSalesStateGroupRules();
  const {
    isOpen: isHelpOpen,
    onOpen: onHelpOpen,
    onClose: onHelpClose,
  } = useDisclosure();

  const [sortKey, setSortKey] = useState<'name' | 'dsp' | 'none'>('none');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [searchText, setSearchText] = useState('');
  const [showUngrouped, setShowUngrouped] = useState(false);

  const selectedBrand = brands.find((b) => b.brand_id === selectedBrandId);
  const stores = useMemo(() => selectedBrand?.stores ?? [], [selectedBrand]);

  // ブランド一覧が読み込まれたら先頭を自動選択
  useEffect(() => {
    if (brands.length > 0 && selectedBrandId === undefined) {
      setSelectedBrandId(brands[0].brand_id);
    }
  }, [brands, selectedBrandId]);

  // ブランド選択時に先頭店舗を自動選択 + 商品データ取得
  useEffect(() => {
    if (!selectedBrandId) {
      setAllItems([]);
      setSelectedStoreName('');
      return;
    }
    const brand = brands.find((b) => b.brand_id === selectedBrandId);
    const firstStore = brand?.stores?.[0];
    setSelectedStoreName(firstStore?.store_name ?? '');

    let cancelled = false;
    (async () => {
      setIsItemsLoading(true);
      try {
        const service = new LinkItemService(getClient());
        const items = await service.fetch();
        if (!cancelled) setAllItems(items);
      } catch {
        if (!cancelled) setAllItems([]);
      } finally {
        if (!cancelled) setIsItemsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBrandId, brands]);

  useEffect(() => {
    if (rulesData) {
      setRulesText(rulesData.rules_text);
    } else {
      setRulesText('');
    }
  }, [rulesData]);

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const brandId = Number(e.target.value);
    if (brandId) {
      setSelectedBrandId(brandId);
    } else {
      setSelectedBrandId(undefined);
      setRulesText('');
    }
  };

  const parseResult = useMemo(
    () => parseBulkSoldoutGroupDef(rulesText),
    [rulesText]
  );
  const { groups, errors } = parseResult;

  const handleSave = async () => {
    if (selectedBrandId === undefined) return;
    if (errors.length > 0) {
      window.alert(
        'パースエラーがあります。エラーを修正してから保存してください。'
      );
      return;
    }
    try {
      await upsertRules({ brandId: selectedBrandId, rulesText });
      await refetch();
      window.alert('グルーピングルールを保存しました。');
    } catch {
      // onError で alert 済み
    }
  };

  // APIデータ → FlatItem[]
  const flatItems: FlatItem[] = useMemo(() => {
    return linkItemsToFlatItems(allItems, selectedStoreName || undefined);
  }, [allItems, selectedStoreName]);

  // グルーピングプレビュー: グループごとのマッチ商品
  const groupsWithMatches: GroupWithMatches[] = useMemo(() => {
    return groups.map((group) => {
      const matches: FlatItem[] = [];
      for (const fi of flatItems) {
        for (const rule of group.rules) {
          if (matchesRule(fi.name, rule)) {
            matches.push(fi);
            break;
          }
        }
      }
      return { group, matches };
    });
  }, [groups, flatItems]);

  // 商品名 → 所属グループ名のマップ
  const itemGroupMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const gm of groupsWithMatches) {
      for (const fi of gm.matches) {
        const key = `${fi.dsp}:${fi.name}`;
        const existing = map.get(key) ?? [];
        existing.push(gm.group.name);
        map.set(key, existing);
      }
    }
    return map;
  }, [groupsWithMatches]);

  // 商品一覧（検索・フィルタ・ソート + 所属グループ列）
  const itemRows = useMemo(() => {
    const withGroups = flatItems.map((fi) => ({
      ...fi,
      groups: itemGroupMap.get(`${fi.dsp}:${fi.name}`) ?? [],
    }));

    const searched = searchText.trim()
      ? withGroups.filter((row) =>
          row.name.toLowerCase().includes(searchText.trim().toLowerCase())
        )
      : withGroups;

    const filtered = showUngrouped
      ? searched.filter((row) => row.groups.length === 0)
      : searched;

    if (sortKey === 'none') return filtered;

    return [...filtered].sort((a, b) => {
      const aVal = sortKey === 'name' ? a.name : a.dsp;
      const bVal = sortKey === 'name' ? b.name : b.dsp;
      const cmp = aVal.localeCompare(bVal, 'ja');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [searchText, showUngrouped, sortKey, sortDir, flatItems, itemGroupMap]);

  const isLoading =
    isEnterpriseLoading || isRulesLoading || isUpsertLoading || isItemsLoading;
  const hasChanges = rulesText !== (rulesData?.rules_text ?? '');
  const contentHeight = 'calc(100vh - 280px)';
  const displayStoreName = selectedStoreName || '全店舗';

  return (
    <PageLayout title={routes.LinkItemsV2.title}>
      <CmBox pt="20px">
        {/* ヘッダー: ブランド | 店舗 | 操作ガイド */}
        <CmFlex align="center" mb="16px" gridGap={3}>
          <CmSelect
            value={selectedBrandId ?? ''}
            onChange={handleBrandChange}
            w="220px"
            size="sm"
          >
            {brands.map((brand) => (
              <option key={brand.brand_id} value={brand.brand_id}>
                {brand.brand_name}
              </option>
            ))}
          </CmSelect>
          {selectedBrandId !== undefined && stores.length > 0 && (
            <CmSelect
              value={selectedStoreName}
              onChange={(e) => setSelectedStoreName(e.target.value)}
              w="180px"
              size="sm"
            >
              {stores.map((store) => (
                <option key={store.master_store_id} value={store.store_name}>
                  {store.store_name}
                </option>
              ))}
            </CmSelect>
          )}
          <CmBox
            as="button"
            onClick={onHelpOpen}
            display="flex"
            alignItems="center"
            px={2}
            py={1}
            border="1px solid"
            borderColor="gray.400"
            borderRadius="md"
            color="gray.600"
            fontSize="xs"
            bg="white"
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
            flexShrink={0}
          >
            <QuestionOutlineIcon color="gray.600" mr={1} />
            操作ガイド
          </CmBox>
        </CmFlex>

        <HelpModal isOpen={isHelpOpen} onClose={onHelpClose} />

        {selectedBrandId === undefined && (
          <CmMessageCard margin="50px auto 0">
            ブランドを選択してください。
          </CmMessageCard>
        )}

        {selectedBrandId !== undefined && (
          <CmFlex gridGap={4} alignItems="flex-start">
            {/* 左カラム: グルーピング定義 */}
            <CmBox flex={2} minW={0}>
              <CmText fontWeight="bold" fontSize="sm" mb={2}>
                グルーピング定義
              </CmText>
              <Textarea
                value={rulesText}
                onChange={(e) => setRulesText(e.target.value)}
                bg="white"
                fontFamily="monospace"
                fontSize="xs"
                h={contentHeight}
                resize="none"
                placeholder={`[グループ名]\n商品名\n商品名*\n*部分一致*`}
              />
              <CmFlex justifyContent="flex-end" mt={2}>
                <CmButton
                  size="xs"
                  onClick={handleSave}
                  isDisabled={!hasChanges}
                >
                  保存
                </CmButton>
              </CmFlex>
            </CmBox>

            {/* 中央カラム: グルーピングプレビュー */}
            <CmBox flex={3} minW={0}>
              <CmFlex align="baseline" mb={2} gridGap={2}>
                <CmText fontWeight="bold" fontSize="sm">
                  グルーピングプレビュー（{displayStoreName}）
                </CmText>
                <CmText fontSize="xs" color="gray.500">
                  {groups.length} グループ /{' '}
                  {groups.reduce((s, g) => s + g.rules.length, 0)} ルール
                  {errors.length > 0 && (
                    <CmText as="span" color="red.500" ml={1}>
                      ({errors.length} エラー)
                    </CmText>
                  )}
                </CmText>
              </CmFlex>

              {errors.length > 0 && (
                <CmBox mb={3} p={2} bg="red.50" borderRadius="md">
                  {errors.map((err, i) => (
                    <CmText key={i} fontSize="xs" color="red.600">
                      行 {err.line}: {err.message}
                    </CmText>
                  ))}
                </CmBox>
              )}

              <CmBox maxH={contentHeight} overflowY="auto">
                <Accordion allowMultiple defaultIndex={[]}>
                  {groupsWithMatches.map((gm, i) => (
                    <AccordionItem
                      key={i}
                      mb={2}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      overflow="hidden"
                    >
                      <AccordionButton
                        py={2}
                        px={4}
                        bg="gray.50"
                        _hover={{ bg: 'gray.100' }}
                      >
                        <CmFlex flex="1" align="center" mr={2}>
                          <CmText fontWeight="bold" fontSize="sm">
                            {gm.group.name}
                          </CmText>
                          <Badge ml={2} colorScheme="gray" fontSize="xs">
                            {gm.matches.length}件
                          </Badge>
                        </CmFlex>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel p={0}>
                        {/* ルール定義表示 */}
                        <CmBox
                          px={4}
                          py={2}
                          bg="gray.50"
                          borderTop="1px solid"
                          borderColor="gray.100"
                        >
                          {gm.group.rules.map((rule, j) => (
                            <CmFlex key={j} align="center" mb={0.5}>
                              <MatchTypeBadge rule={rule} />
                              <CmText fontSize="xs" fontFamily="monospace">
                                {rule.raw}
                              </CmText>
                              <CmText fontSize="xs" color="gray.400" ml={2}>
                                {matchExample(rule)}
                              </CmText>
                            </CmFlex>
                          ))}
                        </CmBox>
                        {/* マッチ商品 */}
                        {gm.matches.map((fi) => (
                          <CmFlex
                            key={fi.key}
                            align="center"
                            py={2}
                            px={4}
                            bg="white"
                            borderTop="1px solid"
                            borderColor="gray.100"
                            _hover={{ bg: 'gray.50' }}
                          >
                            <CmText
                              color="gray.500"
                              fontSize="xs"
                              minW="80px"
                              mr={2}
                            >
                              {fi.dsp}
                            </CmText>
                            <Badge
                              colorScheme={fi.isTopping ? 'orange' : 'cyan'}
                              mr={2}
                              fontSize="xs"
                            >
                              {fi.isTopping ? 'オプション' : '単品'}
                            </Badge>
                            <CmText fontSize="sm" flex="1">
                              {fi.name}
                            </CmText>
                          </CmFlex>
                        ))}
                        {gm.matches.length === 0 && (
                          <CmText fontSize="xs" color="gray.400" px={4} py={2}>
                            マッチする商品がありません
                          </CmText>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
                {groups.length === 0 && (
                  <CmText color="gray.400" fontSize="sm">
                    定義を入力するとプレビューが表示されます
                  </CmText>
                )}
              </CmBox>
            </CmBox>

            {/* 右カラム: 商品一覧 */}
            <CmBox flex={4} minW={0}>
              <CmText fontWeight="bold" fontSize="sm" mb={2}>
                商品一覧（{displayStoreName}）
              </CmText>

              <CmFlex align="center" mb={2} justifyContent="space-between">
                <CmInput
                  size="sm"
                  placeholder="商品名で検索"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  flex={1}
                  mr={3}
                  bg="white"
                />
                <CmFlex align="center" flexShrink={0}>
                  <Switch
                    id="ungrouped-filter"
                    size="sm"
                    isChecked={showUngrouped}
                    onChange={(e) => setShowUngrouped(e.target.checked)}
                    colorScheme="orange"
                  />
                  <FormLabel
                    ml={1}
                    htmlFor="ungrouped-filter"
                    mb={0}
                    fontSize="xs"
                    cursor="pointer"
                    whiteSpace="nowrap"
                  >
                    未グルーピング
                  </FormLabel>
                  <CmText
                    fontSize="xs"
                    color="gray.400"
                    ml={2}
                    whiteSpace="nowrap"
                  >
                    {itemRows.length}件
                  </CmText>
                </CmFlex>
              </CmFlex>

              {/* ヘッダー行 */}
              <CmFlex
                px={2}
                py={1}
                bg="gray.100"
                borderBottom="1px solid"
                borderColor="gray.300"
              >
                {(
                  [
                    { key: 'dsp', label: 'DSP', w: '90px' },
                    { key: null, label: '種別', w: '70px' },
                    { key: 'name', label: '商品名', w: undefined },
                    { key: null, label: 'グループ', w: undefined },
                  ] as const
                ).map((col, ci) => {
                  const isActive = col.key !== null && sortKey === col.key;
                  const isAsc = isActive && sortDir === 'asc';
                  const isDesc = isActive && sortDir === 'desc';
                  return (
                    <CmFlex
                      key={ci}
                      align="center"
                      w={col.w}
                      flex={col.w ? undefined : '1'}
                      flexShrink={col.w ? 0 : undefined}
                      mr={2}
                      cursor={col.key !== null ? 'pointer' : 'default'}
                      userSelect="none"
                      color={isActive ? 'blue.600' : 'gray.600'}
                      _hover={col.key !== null ? { color: 'blue.500' } : {}}
                      onClick={() => {
                        if (col.key === null) return;
                        if (sortKey !== col.key) {
                          setSortKey(col.key);
                          setSortDir('asc');
                        } else if (sortDir === 'asc') {
                          setSortDir('desc');
                        } else {
                          setSortKey('none');
                        }
                      }}
                    >
                      <CmText
                        fontSize="xs"
                        fontWeight="bold"
                        color="inherit"
                        mr={col.key !== null ? 0.5 : 0}
                      >
                        {col.label}
                      </CmText>
                      {col.key !== null &&
                        (isAsc ? (
                          <ChevronUpIcon boxSize={3} />
                        ) : isDesc ? (
                          <ChevronDownIcon boxSize={3} />
                        ) : (
                          <UpDownIcon boxSize={3} color="gray.400" />
                        ))}
                    </CmFlex>
                  );
                })}
              </CmFlex>

              {/* 商品行 */}
              <CmBox maxH={contentHeight} overflowY="auto">
                {itemRows.length === 0 ? (
                  <CmText fontSize="xs" color="gray.400" p={3}>
                    該当する商品がありません
                  </CmText>
                ) : (
                  itemRows.map((row, i) => (
                    <CmFlex
                      key={i}
                      align="center"
                      px={2}
                      py={1.5}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      _hover={{ bg: 'gray.50' }}
                    >
                      <CmText
                        fontSize="xs"
                        color="gray.500"
                        w="90px"
                        flexShrink={0}
                        mr={2}
                      >
                        {row.dsp}
                      </CmText>
                      <Badge
                        colorScheme={row.isTopping ? 'orange' : 'cyan'}
                        w="70px"
                        flexShrink={0}
                        mr={2}
                        textAlign="center"
                        fontSize="xs"
                      >
                        {row.isTopping ? 'オプション' : '単品'}
                      </Badge>
                      <CmText fontSize="xs" flex="1" mr={2}>
                        {row.name}
                      </CmText>
                      <CmBox flex="1">
                        {row.groups.length > 0 ? (
                          row.groups.map((g, gi) => (
                            <Badge
                              key={gi}
                              colorScheme="blue"
                              fontSize="xs"
                              mr={0.5}
                              mb={0.5}
                            >
                              {g}
                            </Badge>
                          ))
                        ) : (
                          <CmText fontSize="xs" color="gray.300">
                            —
                          </CmText>
                        )}
                      </CmBox>
                    </CmFlex>
                  ))
                )}
              </CmBox>
            </CmBox>
          </CmFlex>
        )}
      </CmBox>

      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default LinkItemsV2Page;
